import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarOff, Clock, Eye, LogOut, MessageCircle, Plus, Printer, Trash2, X } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";
import { mergeSettings, WEEK_ORDER, DAY_LABELS, slotsForDate, romeToday, romeNow, patientWhatsAppUrl, buildAdminConfirmMessage, buildAdminRejectMessage, buildAdminContactMessage } from "@/lib/site";
import CmsTab from "@/pages/admin/CmsTab";
import HistoryTab from "@/pages/admin/HistoryTab";
import AccountTab from "@/pages/admin/AccountTab";
import PanelConfigTab from "@/pages/admin/PanelConfigTab";
import AdminMobileNav from "@/pages/admin/AdminMobileNav";
import RequestsTab from "@/pages/admin/RequestsTab";
import PromemoriaTab from "@/pages/admin/PromemoriaTab";
import ApptRow, { STATUS_FILTERS } from "@/pages/admin/ApptRow";
import GoogleTab from "@/pages/admin/GoogleTab";
import NotificheTab from "@/pages/admin/NotificheTab";
import WhatsAppTab from "@/pages/admin/WhatsAppTab";

const SUB_TABS_KEYS = ["richieste", "promemoria", "storico"];

const TABS = [
  { key: "appuntamenti", label: "Appuntamenti" },
  { key: "sito", label: "Gestione sito" },
  { key: "servizi", label: "Servizi" },
  { key: "orari", label: "Orari & slot" },
  { key: "blocchi", label: "Date bloccate" },
  { key: "calendario", label: "Calendario" },
  { key: "notifiche", label: "Notifiche" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "account", label: "Account e sicurezza" },
];

export default function Admin() {
  const [auth, setAuth] = useState("checking");
  const [tab, setTab] = useState("appuntamenti");
  const [dirty, setDirty] = useState(false);
  const [draft, setDraft] = useState(null);
  const [services, setServices] = useState([]);
  const [status, setStatus] = useState(null);
  const [panel, setPanel] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const prevPendingRef = useRef(null);

  useEffect(() => {
    if (auth === true) {
      api.get("/admin/notifications").then(({ data }) => setNotifPrefs(data.prefs)).catch(() => {});
    }
  }, [auth]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const g = searchParams.get("google");
    if (g === "collegato") {
      flash("Google collegato con successo: i calendari sono attivi");
      setSearchParams({}, { replace: true });
    } else if (g === "errore") {
      flash("Il collegamento Google non è riuscito: riprova dalla scheda Calendario", false);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPending = () => {
    api
      .get("/admin/appointments")
      .then(({ data }) => {
        const pending = (data.appointments || []).filter((a) => a.status === "pending").length;
        const prev = prevPendingRef.current;
        prevPendingRef.current = pending;
        setPendingCount(pending);
        if (prev !== null && pending > prev && notifPrefs?.newRequest?.browser && "Notification" in window && Notification.permission === "granted") {
          try {
            new Notification("K Nutrition", { body: "Nuova richiesta di appuntamento da confermare." });
          } catch (e) { /* ignora */ }
        }
      })
      .catch(() => {});
  };

  const flash = (msg, ok = true) => {
    setStatus({ msg, ok });
    setTimeout(() => setStatus(null), 3500);
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then(() => setAuth(true))
      .catch(() => setAuth(false));
  }, []);

  const loadSite = async () => {
    const { data } = await api.get("/admin/draft");
    setDraft(mergeSettings(data.settings));
    setDirty(data.dirty);
    const site = await api.get("/site");
    setServices(site.data.services || []);
  };

  const loadPanel = async () => {
    const { data } = await api.get("/admin/panel-config");
    setPanel(data);
  };

  const publishChanges = async () => {
    try {
      const { data } = await api.post("/admin/publish");
      setDirty(false);
      flash(`Sito pubblicato (versione v${data.version}): le modifiche sono online`);
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const discardChanges = async () => {
    try {
      await api.post("/admin/discard");
      await loadSite();
      flash("Bozza annullata: il sito resta com'era");
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  useEffect(() => {
    if (auth === true) {
      loadSite().catch(() => flash("Errore nel caricamento", false));
      loadPanel().catch(() => setPanel(null));
      loadPending();
      const t = setInterval(loadPending, 30000);
      return () => clearInterval(t);
    }
  }, [auth]);

  const navTabs = useMemo(() => {
    const badgeFor = (key) => (key === "appuntamenti" ? pendingCount : 0);
    if (panel) {
      const base = panel.tabs
        .filter((t) => t.visible && !SUB_TABS_KEYS.includes(t.key))
        .map((t) => ({ key: t.key, label: t.label, badge: badgeFor(t.key) }));
      const pages = (panel.pages || [])
        .filter((p) => p.visible)
        .map((p) => ({ key: `page-${p.pid}`, label: p.title, isPage: true, page: p, badge: 0 }));
      return [...base, ...pages];
    }
    return TABS.map((t) => ({ ...t, badge: badgeFor(t.key) }));
  }, [panel, pendingCount]);

  const activePage = navTabs.find((t) => t.key === tab && t.isPage)?.page;

  useEffect(() => {
    if (panel && !navTabs.some((t) => t.key === tab)) setTab("appuntamenti");
  }, [panel, navTabs, tab]);

  if (auth === "checking") {
    return <div className="min-h-screen bg-ivory" data-testid="admin-checking" />;
  }

  if (auth === false) {
    return <Login onLogin={() => setAuth(true)} />;
  }

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/admin/settings", draft);
      setDraft(mergeSettings(data.settings));
      setDirty(true);
      flash("Bozza salvata: usa Anteprima e Pubblica per portarla online");
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory text-charcoal" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-ivory/90 backdrop-blur-xl border-b border-taupe/25">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/images/logo-transparent.png" alt="K Nutrition" className="h-9" />
            <span className="text-[11px] uppercase tracking-widest2 text-taupe font-medium hidden sm:inline">Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[12px] uppercase tracking-widest2 text-charcoal/60 hover:text-deeptaupe flex items-center gap-1.5" data-testid="admin-view-site">
              <ArrowLeft size={14} /> Sito
            </Link>
            <button
              onClick={async () => {
                await api.post("/auth/logout").catch(() => {});
                setAuth(false);
              }}
              data-testid="admin-logout"
              className="flex items-center gap-1.5 text-[12px] uppercase tracking-widest2 text-deeptaupe hover:text-charcoal"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8 pb-28 md:pb-8">
        {status && (
          <p
            className={`mb-6 rounded-xl px-5 py-3 text-sm ${status.ok ? "bg-peach text-deeptaupe border border-taupe/30" : "bg-red-50 text-red-700 border border-red-200"}`}
            data-testid="admin-status"
            role="status"
          >
            {status.msg}
          </p>
        )}

        <nav className="hidden md:flex gap-2 overflow-x-auto pb-2 mb-8" data-testid="admin-tabs">
          {navTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`shrink-0 inline-flex items-center gap-0 rounded-full px-5 py-3 sm:py-2.5 text-[12px] uppercase tracking-widest2 font-medium transition-colors duration-300 ${
                tab === t.key ? "bg-deeptaupe text-ivory" : "border border-taupe/40 text-charcoal/70 hover:bg-peach"
              }`}
            >
              {t.label}
              {t.badge > 0 && (
                <span
                  className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium ${tab === t.key ? "bg-peach text-deeptaupe" : "bg-deeptaupe text-ivory"}`}
                  data-testid={`admin-tab-badge-${t.key}`}
                >
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {dirty && (
          <div className="sticky top-16 z-30 -mx-5 px-5 py-4 mb-6 bg-peach/95 backdrop-blur border-y border-taupe/25 flex flex-wrap items-center gap-3" data-testid="cms-publish-bar">
            <p className="text-[13px] text-charcoal font-medium max-w-md">
              Hai modifiche non pubblicate: il sito mostra ancora l'ultima versione pubblicata.
            </p>
            <span className="ml-auto flex flex-wrap gap-2 w-full sm:w-auto">
              <button onClick={() => window.open("/?preview=1", "_blank", "noopener")} data-testid="cms-preview" className="flex-1 justify-center sm:flex-none inline-flex items-center gap-2 rounded-full border border-taupe/40 text-charcoal/70 text-[12px] uppercase tracking-widest2 font-medium px-5 py-3 sm:py-2.5 hover:bg-ivory transition-colors">
                <Eye size={14} /> Anteprima
              </button>
              <button onClick={discardChanges} data-testid="cms-discard" className="flex-1 justify-center sm:flex-none rounded-full border border-taupe/40 text-charcoal/70 text-[12px] uppercase tracking-widest2 font-medium px-5 py-3 sm:py-2.5 hover:bg-ivory transition-colors">
                Annulla
              </button>
              <button onClick={publishChanges} data-testid="cms-publish" className="flex-1 justify-center sm:flex-none rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 sm:py-2.5 hover:bg-charcoal transition-colors">
                Pubblica
              </button>
            </span>
          </div>
        )}

        {!draft ? (
          <p className="text-charcoal/50">Caricamento…</p>
        ) : (
          <>
            {tab === "appuntamenti" && <AppuntamentiGroup flash={flash} contact={draft.contact} panel={panel} pendingCount={pendingCount} />}

            {tab === "orari" && (
              <div className="space-y-10">
                <Section title="Orari dello studio (appuntamenti in presenza)" description="Le finestre di richiesta per gli appuntamenti in studio. Gli orari di inizio disponibili vengono generati automaticamente in base alla durata del servizio." />
                <HoursEditor hours={draft.hours} onChange={(h) => setDraft({ ...draft, hours: h })} />
                <div className="border-t border-taupe/20 pt-8">
                  <Section title="Consulenze online" description="Configura finestre dedicate alle consulenze online. Finché non vengono configurate, ai visitatori viene chiesto di indicare le preferenze senza mostrare disponibilità inventate." />
                  <label className="flex items-center gap-3 mb-6 text-sm" data-testid="admin-online-configured">
                    <input
                      type="checkbox"
                      checked={!!draft.onlineConfigured}
                      onChange={(e) => setDraft({ ...draft, onlineConfigured: e.target.checked })}
                      className="w-4 h-4 accent-[#6B5C50]"
                    />
                    Orari online confermati (mostra slot generati)
                  </label>
                  <HoursEditor hours={draft.onlineHours} onChange={(h) => setDraft({ ...draft, onlineHours: h })} />
                  <div className="mt-6">
                    <Field label="Testo informativo orari online" textarea value={draft.onlineNotice || ""} onChange={(v) => setDraft({ ...draft, onlineNotice: v })} />
                  </div>
                </div>
                <div className="border-t border-taupe/20 pt-8">
                  <Section title="Incremento slot (minuti)" description="Passo con cui vengono generati gli orari di inizio richiedibili." />
                  <select
                    value={draft.slotIncrement || 15}
                    onChange={(e) => setDraft({ ...draft, slotIncrement: Number(e.target.value) })}
                    data-testid="admin-slot-increment"
                    className="rounded-xl border border-taupe/40 bg-transparent px-4 py-2.5 text-sm"
                  >
                    {[10, 15, 20, 30].map((v) => (
                      <option key={v} value={v}>{v} minuti</option>
                    ))}
                  </select>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
                    <label className="block">
                      <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Cuscino tra appuntamenti (minuti)</span>
                      <input
                        type="number"
                        min="0"
                        value={draft.bufferMinutes ?? 0}
                        onChange={(e) => setDraft({ ...draft, bufferMinutes: Number(e.target.value) })}
                        data-testid="admin-buffer-minutes"
                        className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Scadenza richieste in attesa</span>
                      <span className="flex items-center gap-3 text-sm">
                        <input
                          type="checkbox"
                          checked={(draft.pendingExpiryMinutes ?? 1440) > 0}
                          onChange={(e) => setDraft({ ...draft, pendingExpiryMinutes: e.target.checked ? 1440 : 0 })}
                          className="w-4 h-4 accent-[#6B5C50]"
                          data-testid="admin-pending-expiry-enabled"
                        />
                        Scadenza automatica attiva
                      </span>
                      {(draft.pendingExpiryMinutes ?? 1440) > 0 && (
                        <span className="flex items-center gap-2 mt-3">
                          <input
                            type="number"
                            min="1"
                            value={Math.round((draft.pendingExpiryMinutes ?? 1440) / 60)}
                            onChange={(e) => setDraft({ ...draft, pendingExpiryMinutes: Number(e.target.value) * 60 })}
                            data-testid="admin-pending-expiry"
                            className="w-24 rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm"
                          />
                          <span className="text-[13px] text-charcoal/60">ore: le richieste non confermate liberano l&apos;orario dopo questo tempo</span>
                        </span>
                      )}
                    </label>
                  </div>
                  <SlotPreview settings={draft} />
                </div>
                <SaveButton onClick={saveSettings} testId="admin-save-orari" />
              </div>
            )}

            {tab === "servizi" && <ServicesTab services={services} reload={(s) => setServices(s)} flash={flash} />}

            {tab === "blocchi" && (
              <div className="space-y-6">
                <Section title="Giorni chiusi o festività" description="Le date bloccate non sono selezionabili nel calendario delle richieste (in studio e online)." />
                <BlockedDates dates={draft.blockedDates || []} onChange={(d) => setDraft({ ...draft, blockedDates: d })} />
                <div className="border-t border-taupe/20 pt-8">
                  <Section title="Blocchi orari (pause e ore bloccate)" description="Blocca intervalli orari specifici di una data: gli slot sovrapposti scompaiono subito dal calendario pubblico. Lascia gli orari vuoti per bloccare l'intera giornata." />
                  <SlotBlocks flash={flash} />
                </div>
                <SaveButton onClick={saveSettings} testId="admin-save-blocchi" />
              </div>
            )}

            {tab === "sito" && <CmsTab draft={draft} setDraft={setDraft} saveSettings={saveSettings} flash={flash} />}

            {tab === "calendario" && <GoogleTab flash={flash} />}

            {tab === "notifiche" && <NotificheTab flash={flash} />}

            {tab === "whatsapp" && <WhatsAppTab flash={flash} />}

            {tab === "account" && <AccountTab flash={flash} />}

            {tab === "impostazioni" && <PanelConfigTab flash={flash} reloadPanel={loadPanel} />}

            {activePage && <CustomPanelPage page={activePage} />}
          </>
        )}
      </div>

      <AdminMobileNav
        tabs={navTabs}
        activeTab={tab}
        onPick={setTab}
        onLogout={async () => {
          await api.post("/auth/logout").catch(() => {});
          setAuth(false);
        }}
      />
    </div>
  );
}

/* ---------- custom panel pages ---------- */

/* ---------- gruppo appuntamenti: calendario + richieste + promemoria + storico ---------- */

const APPT_SUBS = [
  { key: "calendario", label: "Calendario" },
  { key: "richieste", label: "Richieste" },
  { key: "promemoria", label: "Promemoria" },
  { key: "storico", label: "Storico" },
];

function AppuntamentiGroup({ flash, contact, panel, pendingCount }) {
  const [sub, setSub] = useState("calendario");
  const panelTabs = panel?.tabs || [];
  const subs = APPT_SUBS
    .filter((s) => s.key === "calendario" || (panelTabs.find((t) => t.key === s.key)?.visible ?? true))
    .map((s) => ({
      ...s,
      label: s.key === "calendario" ? s.label : panelTabs.find((t) => t.key === s.key)?.label || s.label,
      badge: s.key === "richieste" ? pendingCount : 0,
    }));
  return (
    <div>
      <nav className="flex gap-2 overflow-x-auto pb-2 mb-8" data-testid="appts-subtabs">
        {subs.map((s) => (
          <button
            key={s.key}
            onClick={() => setSub(s.key)}
            data-testid={`appts-subtab-${s.key}`}
            className={`shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2 text-[12px] uppercase tracking-widest2 font-medium transition-colors duration-300 ${sub === s.key ? "bg-charcoal text-ivory" : "border border-taupe/40 text-charcoal/70 hover:bg-peach"}`}
          >
            {s.label}
            {s.badge > 0 && (
              <span
                className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium ${sub === s.key ? "bg-peach text-deeptaupe" : "bg-charcoal text-ivory"}`}
                data-testid={`appts-subtab-badge-${s.key}`}
              >
                {s.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      {sub === "calendario" && <AppointmentsTab flash={flash} contact={contact} />}
      {sub === "richieste" && <RequestsTab flash={flash} contact={contact} />}
      {sub === "promemoria" && <PromemoriaTab flash={flash} contact={contact} />}
      {sub === "storico" && <HistoryTab flash={flash} />}
    </div>
  );
}

function CustomPanelPage({ page }) {
  return (
    <div>
      <Section title={page.title} description="Pagina interna del pannello: puoi modificare titolo e contenuto dalla scheda Impostazioni." />
      <div className="bg-white rounded-[24px] shadow-soft p-8 whitespace-pre-wrap text-[15px] leading-relaxed" data-testid={`admin-custom-page-${page.pid}`}>
        {page.content || <span className="text-charcoal/50">Nessun contenuto ancora: scrivilo dalla scheda Impostazioni.</span>}
      </div>
    </div>
  );
}

/* ---------- appointments management ---------- */

function AppointmentsTab({ flash, contact }) {
  const [view, setView] = useState("day");
  const [anchor, setAnchor] = useState(romeToday());
  const [items, setItems] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    const d = new Date(anchor + "T12:00:00");
    if (view === "day") return [anchor, anchor];
    if (view === "week") {
      const dow = (d.getDay() + 6) % 7;
      const mon = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow);
      const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
      return [mon.toLocaleDateString("en-CA"), sun.toLocaleDateString("en-CA")];
    }
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return [first.toLocaleDateString("en-CA"), last.toLocaleDateString("en-CA")];
  }, [view, anchor]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/appointments", { params: { from_date: range[0], to_date: range[1] } });
      setItems(data.appointments);
      const site = await api.get("/site");
      setServicesList((site.data.services || []).filter((x) => x.active !== false));
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [view, anchor]);

  const shiftAnchor = (dir) => {
    const d = new Date(anchor + "T12:00:00");
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else if (view === "year") d.setFullYear(d.getFullYear() + dir);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(d.toLocaleDateString("en-CA"));
  };

  const setStatus = async (a, status) => {
    try {
      await api.put(`/admin/appointments/${a.mid}`, {
        service_id: a.service_id, date: a.date, start: a.start, name: a.name, phone: a.phone || "", email: a.email || "", status,
      });
      flash(
        status === "confirmed" ? "Appuntamento confermato"
          : status === "rejected" ? "Richiesta rifiutata: orario liberato"
            : status === "cancelled" ? "Appuntamento annullato: orario liberato"
              : "Stato aggiornato",
      );
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare definitivamente l'appuntamento di ${a.name}?`)) return;
    try {
      await api.delete(`/admin/appointments/${a.mid}`);
      flash("Appuntamento eliminato");
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const saveForm = async () => {
    try {
      if (form.mid) {
        await api.put(`/admin/appointments/${form.mid}`, form);
        flash("Appuntamento aggiornato");
      } else {
        await api.post("/admin/appointments", form);
        flash("Appuntamento creato");
      }
      setForm(null);
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const dayLabel = (ds) =>
    new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "numeric", month: "long" }).format(new Date(ds + "T12:00:00"));

  const modeOf = (a) => servicesList.find((s) => s.id === a.service_id)?.mode || "studio";

  const [statusFilter, setStatusFilter] = useState("tutti");
  const filteredItems = useMemo(
    () => (statusFilter === "tutti" ? items : items.filter((a) => a.status === statusFilter)),
    [items, statusFilter],
  );
  const inRange = filteredItems.filter((a) => (view === "day" ? a.date === anchor : true));

  const [personalBusy, setPersonalBusy] = useState({ blocks: [], verified: true });

  useEffect(() => {
    if (view === "month" || view === "year") return undefined;
    let cancelled = false;
    api
      .get("/admin/google/busy", { params: { from_date: range[0], to_date: range[1] } })
      .then(({ data }) => {
        if (!cancelled) setPersonalBusy({ blocks: data.busy || [], verified: data.verified !== false });
      })
      .catch(() => {
        if (!cancelled) setPersonalBusy({ blocks: [], verified: false });
      });
    return () => {
      cancelled = true;
    };
  }, [view, range[0], range[1]]);

  const dayBusy = personalBusy.blocks.filter((b) => b.date === anchor);

  return (
    <div>
      <div className="hidden md:block">
        <Section title="Gestione appuntamenti" description="Vista giornaliera, settimanale e mensile. Aggiungi appuntamenti ricevuti via telefono, WhatsApp o di persona; approva o rifiuta le richieste arrivate dal sito: l'orario si blocca finché non decidi." />
      </div>

      <div
        className="sticky top-16 z-30 -mx-5 px-5 pt-2 pb-2 bg-ivory/95 backdrop-blur-xl border-b border-taupe/25 md:static md:mx-0 md:px-0 md:py-0 md:bg-transparent md:backdrop-blur-none md:border-0"
        data-testid="admin-appts-header"
      >
        <p className="md:hidden text-center text-[10px] uppercase tracking-widest2 font-medium text-charcoal/50 mb-2" data-testid="admin-appts-mobile-title">
          Gestisci appuntamenti
        </p>

        <div className="flex flex-wrap items-center gap-1.5 md:gap-3 md:mb-6" data-testid="admin-appts-toolbar">
          <div className="order-1 w-full md:order-none md:w-auto flex items-center gap-1 rounded-full border border-taupe/30 p-1">
            {["day", "week", "month", "year"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`admin-appts-view-${v}`}
                className={`flex-1 md:flex-none text-center rounded-full px-2.5 py-1.5 md:px-4 text-[11px] md:text-[12px] uppercase tracking-widest2 font-medium transition-colors ${view === v ? "bg-deeptaupe text-ivory" : "text-charcoal/60 hover:text-deeptaupe"}`}
              >
                {v === "day" ? "Giorno" : v === "week" ? "Settimana" : v === "month" ? "Mese" : "Anno"}
              </button>
            ))}
          </div>
          <div className="order-2 w-full md:order-none md:w-auto flex items-center justify-center md:justify-start gap-5 md:gap-2">
            <button onClick={() => shiftAnchor(-1)} data-testid="admin-appts-prev" className="px-4 py-2.5 md:px-3 md:py-2 rounded-full border border-taupe/30 hover:bg-peach" aria-label="Precedente">←</button>
            <button
              onClick={() => setAnchor(romeToday())}
              data-testid="admin-appts-oggi"
              className="rounded-full border border-deeptaupe md:border-deeptaupe/40 bg-peach/60 md:bg-transparent text-deeptaupe text-[13px] md:text-[12px] uppercase tracking-widest2 font-medium px-10 py-3 md:px-5 md:py-2 hover:bg-peach transition-colors"
            >
              Oggi
            </button>
            <button onClick={() => shiftAnchor(1)} data-testid="admin-appts-next" className="px-4 py-2.5 md:px-3 md:py-2 rounded-full border border-taupe/30 hover:bg-peach" aria-label="Successivo">→</button>
          </div>
          <p className="order-3 w-full md:order-none md:w-auto md:flex-none md:min-w-[220px] font-display text-sm md:text-xl capitalize text-center text-charcoal/80 md:text-charcoal" data-testid="admin-appts-period">{dayLabel(range[0])}{range[0] !== range[1] ? ` — ${new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short" }).format(new Date(range[1] + "T12:00:00"))}` : ""}</p>
          <div className="hidden md:flex flex-wrap gap-2 w-auto md:ml-auto">
            <button
              onClick={() => window.open(`/admin/agenda/${anchor}`, "_blank", "noopener")}
              data-testid="admin-print-agenda"
              className="inline-flex items-center gap-2 rounded-full border border-taupe/40 text-charcoal/70 text-[12px] uppercase tracking-widest2 font-medium px-5 py-2.5 hover:bg-peach transition-colors"
            >
              <Printer size={14} /> Stampa agenda
            </button>
            <button
              onClick={() => window.open(`/admin/agenda-settimana/${anchor}`, "_blank", "noopener")}
              data-testid="admin-print-week-agenda"
              className="inline-flex items-center gap-2 rounded-full border border-taupe/40 text-charcoal/70 text-[12px] uppercase tracking-widest2 font-medium px-5 py-2.5 hover:bg-peach transition-colors"
            >
              <Printer size={14} /> Stampa settimana
            </button>
            <button
              onClick={() => setForm({ mid: null, date: anchor, service_id: servicesList[0]?.id || "", start: "10:00", name: "", phone: "", email: "", status: "confirmed" })}
              data-testid="admin-appt-new"
              className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-6 py-2.5 hover:bg-charcoal transition-colors"
            >
              <Plus size={14} /> Nuovo appuntamento
            </button>
          </div>
        </div>

        <nav className="flex gap-1.5 md:gap-2 overflow-x-auto pb-1 md:mb-6" data-testid="admin-appts-status-filters">
          {STATUS_FILTERS.map((f) => {
            const count = items.filter((a) => (f.key === "tutti" ? true : a.status === f.key)).length;
            return (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                data-testid={`admin-appts-filter-${f.key}`}
                className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest2 font-medium transition-colors duration-300 ${statusFilter === f.key ? "bg-charcoal text-ivory" : "bg-white border border-taupe/30 text-charcoal/60 hover:bg-peach"}`}
              >
                {f.label} ({count})
              </button>
            );
          })}
        </nav>
      </div>

      {form && (
        <div className="bg-white rounded-[24px] shadow-soft p-6 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="admin-appt-form">
          <label className="block">
            <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Data</span>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="admin-appt-form-date" className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm" />
          </label>
          <label className="block">
            <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Servizio</span>
            <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} data-testid="admin-appt-form-service" className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm">
              {servicesList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Orario di inizio</span>
            <input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="admin-appt-form-start" className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm" />
          </label>
          <label className="block">
            <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Stato</span>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="admin-appt-form-status" className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm">
              <option value="confirmed">Confermato</option>
              <option value="pending">In attesa</option>
              <option value="rejected">Rifiutato</option>
              <option value="cancelled">Annullato</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Nome</span>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome paziente" data-testid="admin-appt-form-name" className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm" />
          </label>
          <label className="block">
            <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Telefono</span>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 ..." data-testid="admin-appt-form-phone" className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm" />
          </label>
          <label className="block">
            <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Email (facoltativa)</span>
            <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nome@email.it" data-testid="admin-appt-form-email" className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm" />
          </label>
          <div className="sm:col-span-2 flex gap-3">
            <button onClick={saveForm} data-testid="admin-appt-form-save" className="rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors">Salva</button>
            <button onClick={() => setForm(null)} data-testid="admin-appt-form-cancel" className="rounded-full border border-taupe/40 text-charcoal/60 text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-peach">Annulla</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-charcoal/50 text-sm">Caricamento…</p>
      ) : view === "month" ? (
        <MonthGrid anchor={anchor} items={filteredItems} onPick={(ds) => { setAnchor(ds); setView("day"); }} />
      ) : view === "year" ? (
        <YearGrid anchor={anchor} items={filteredItems} onPick={(ds) => { setAnchor(ds); setView("day"); }} />
      ) : (
        <div className="space-y-3" data-testid="admin-appts-list">
          {inRange.length === 0 && dayBusy.length === 0 && <p className="text-charcoal/50 text-sm">Nessun appuntamento nel periodo selezionato.</p>}
          {view === "day" && dayBusy.map((b, i) => (
            <div key={`${b.date}-${i}`} className="rounded-2xl border border-dashed border-taupe/50 bg-taupe/10 px-3 py-2.5 sm:px-4 sm:py-3 mb-2 text-[13px] text-charcoal/60 flex items-center gap-2" data-testid={`admin-personal-block-${i}`}>
              <CalendarOff size={14} className="text-taupe shrink-0" />
              <span className="font-display text-base sm:text-lg w-20 sm:w-28 text-charcoal/70">{b.start}–{b.end}</span>
              Occupato — calendario personale
            </div>
          ))}
          {view === "week"
            ? Object.entries(inRange.reduce((acc, a) => { (acc[a.date] = acc[a.date] || []).push(a); return acc; }, {})).map(([ds, list]) => (
                <div key={ds} className="border border-taupe/25 rounded-2xl p-4">
                  <p className="font-display text-xl capitalize mb-3">{dayLabel(ds)}</p>
                  {list.map((a) => <ApptRow key={a.mid} a={a} onConfirm={setStatus} onCancel={setStatus} onEdit={() => setForm({ mid: a.mid, date: a.date, service_id: a.service_id, start: a.start, name: a.name, phone: a.phone || "", email: a.email || "", status: a.status })} onDelete={remove} mode={modeOf(a)} contact={contact} />)}
                </div>
              ))
            : inRange.map((a) => <ApptRow key={a.mid} a={a} onConfirm={setStatus} onCancel={setStatus} onEdit={() => setForm({ mid: a.mid, date: a.date, service_id: a.service_id, start: a.start, name: a.name, phone: a.phone || "", email: a.email || "", status: a.status })} onDelete={remove} mode={modeOf(a)} contact={contact} />)}
        </div>
      )}

      <button
        onClick={() => {
          setForm({ mid: null, date: anchor, service_id: servicesList[0]?.id || "", start: "10:00", name: "", phone: "", email: "", status: "confirmed" });
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        data-testid="admin-appt-fab"
        aria-label="Nuovo appuntamento"
        className="fixed left-4 bottom-[84px] z-40 md:hidden w-14 h-14 rounded-full bg-deeptaupe text-ivory shadow-soft flex items-center justify-center hover:bg-charcoal active:scale-95 transition-all duration-200"
      >
        <Plus size={26} />
      </button>
    </div>
  );
}

function MonthGrid({ anchor, items, onPick }) {
  const d = new Date(anchor + "T12:00:00");
  const y = d.getFullYear();
  const m = d.getMonth();
  const days = new Date(y, m + 1, 0).getDate();
  const offset = (new Date(y, m, 1).getDay() + 6) % 7;
  const counts = items.reduce((acc, a) => { acc[a.date] = (acc[a.date] || 0) + 1; return acc; }, {});
  return (
    <div>
      <div className="grid grid-cols-7 gap-2 text-center text-[11px] uppercase tracking-widest2 text-charcoal/50 mb-2">
        {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((x) => <span key={x}>{x}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-2" data-testid="admin-appts-month">
        {Array.from({ length: offset }).map((_, i) => <span key={`e${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const ds = `${y}-${String(m + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
          const c = counts[ds] || 0;
          return (
            <button
              key={ds}
              onClick={() => onPick(ds)}
              data-testid={`admin-appts-month-day-${ds}`}
              className={`aspect-square rounded-xl text-sm border transition-colors ${c ? "border-deeptaupe bg-peach/60 font-medium" : "border-taupe/20 text-charcoal/50 hover:bg-peach/30"}`}
            >
              {i + 1}{c > 0 && <span className="block text-[10px] text-deeptaupe">{c} app.</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- year view ---------- */

function YearGrid({ anchor, items, onPick }) {
  const y = new Date(anchor + "T12:00:00").getFullYear();
  const today = romeToday();
  const counts = items.reduce((acc, a) => { acc[a.date] = (acc[a.date] || 0) + 1; return acc; }, {});
  const months = Array.from({ length: 12 }, (_, m) => {
    const first = new Date(y, m, 1);
    return {
      label: new Intl.DateTimeFormat("it-IT", { month: "long" }).format(first),
      days: new Date(y, m + 1, 0).getDate(),
      offset: (first.getDay() + 6) % 7,
    };
  });
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="admin-appts-year">
        {months.map((mo, m) => (
          <div key={m} className="border border-taupe/25 rounded-2xl p-4">
            <p className="font-display text-lg capitalize mb-3">{mo.label} {y}</p>
            <div className="grid grid-cols-7 gap-1 text-center text-[9px] uppercase text-charcoal/40 mb-1">
              {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => <span key={i}>{d}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: mo.offset }).map((_, i) => <span key={`e${i}`} />)}
              {Array.from({ length: mo.days }).map((_, i) => {
                const ds = `${y}-${String(m + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
                const c = counts[ds] || 0;
                return (
                  <button
                    key={ds}
                    onClick={() => onPick(ds)}
                    data-testid={`admin-appts-year-day-${ds}`}
                    className={`aspect-square rounded-md text-[11px] border transition-colors relative ${c ? "border-deeptaupe bg-peach/70 font-medium text-deeptaupe" : ds === today ? "border-charcoal/40 text-charcoal/70" : "border-taupe/15 text-charcoal/50 hover:bg-peach/30"}`}
                  >
                    {i + 1}
                    {c > 0 && (
                      <span className="absolute -top-1 -right-1 bg-deeptaupe text-ivory text-[8px] rounded-full w-3.5 h-3.5 flex items-center justify-center">
                        {c}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[12px] text-charcoal/50 mt-4">Tocca un giorno con appuntamenti per aprirlo nella vista Giorno.</p>
    </div>
  );
}

/* ---------- slot blocks ---------- */

function SlotBlocks({ flash }) {
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({ date: romeToday(), start: "", end: "", reason: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/blocks");
      setBlocks(data.blocks);
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    try {
      await api.post("/admin/blocks", {
        date: form.date,
        start: form.start || null,
        end: form.end || null,
        reason: form.reason,
      });
      setForm({ ...form, start: "", end: "", reason: "" });
      flash("Blocco aggiunto: il calendario pubblico è già aggiornato");
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const remove = async (mid) => {
    try {
      await api.delete(`/admin/blocks/${mid}`);
      flash("Blocco rimosso");
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Data</span>
          <input
            type="date"
            value={form.date}
            min={romeToday()}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="admin-slot-block-date"
            className="rounded-full border border-taupe/40 bg-transparent px-5 py-3 text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Dalle (opz.)</span>
          <input
            type="time"
            value={form.start}
            onChange={(e) => setForm({ ...form, start: e.target.value })}
            data-testid="admin-slot-block-start"
            className="rounded-full border border-taupe/40 bg-transparent px-5 py-3 text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Alle (opz.)</span>
          <input
            type="time"
            value={form.end}
            onChange={(e) => setForm({ ...form, end: e.target.value })}
            data-testid="admin-slot-block-end"
            className="rounded-full border border-taupe/40 bg-transparent px-5 py-3 text-sm"
          />
        </label>
        <label className="block flex-1 min-w-[200px]">
          <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Motivo (interno)</span>
          <input
            type="text"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="es. pausa pranzo"
            data-testid="admin-slot-block-reason"
            className="w-full rounded-full border border-taupe/40 bg-transparent px-5 py-3 text-sm"
          />
        </label>
        <button
          onClick={add}
          data-testid="admin-slot-block-add"
          className="rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors"
        >
          Blocca
        </button>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {blocks.length === 0 && <p className="text-sm text-charcoal/50">Nessun blocco orario attivo.</p>}
        {blocks.map((b) => (
          <span key={b.mid} className="inline-flex items-center gap-2 bg-peach border border-taupe/30 rounded-full px-4 py-2 text-sm" data-testid={`admin-slot-block-chip-${b.mid}`}>
            {new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short" }).format(new Date(b.date + "T12:00:00"))}
            {b.start ? ` · ${b.start}–${b.end}` : " · tutta la giornata"}
            {b.reason && <span className="text-charcoal/50">· {b.reason}</span>}
            <button onClick={() => remove(b.mid)} aria-label="Rimuovi blocco" data-testid={`admin-slot-block-remove-${b.mid}`} className="text-charcoal/50 hover:text-red-600">
              <X size={13} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------- pieces ---------- */

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/login", { email, password });
      onLogin();
    } catch (err) {
      setError(apiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-5" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-peach/50 border border-taupe/30 rounded-3xl p-8" data-testid="admin-login-form">
        <img src="/images/logo-transparent.png" alt="K Nutrition" className="h-14 mb-6" />
        <h1 className="font-display text-3xl mb-2">Area riservata</h1>
        <p className="text-sm text-charcoal/60 mb-8">Dashboard di gestione K Nutrition.</p>
        <label className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-login-email"
          className="w-full rounded-full border border-taupe/40 bg-ivory px-5 py-3 mb-4 text-sm focus:border-deeptaupe focus:outline-none"
        />
        <label className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-login-password"
          className="w-full rounded-full border border-taupe/40 bg-ivory px-5 py-3 mb-6 text-sm focus:border-deeptaupe focus:outline-none"
        />
        {error && (
          <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="w-full rounded-full bg-deeptaupe text-ivory text-[13px] uppercase tracking-widest2 font-medium py-4 transition-colors duration-300 hover:bg-charcoal disabled:opacity-50"
        >
          {busy ? "Accesso…" : "Accedi"}
        </button>
        <Link to="/admin/reset" data-testid="admin-login-forgot" className="block text-center text-[12px] uppercase tracking-widest2 font-medium text-charcoal/50 hover:text-deeptaupe transition-colors mt-4">
          Password dimenticata? Recupera con email o telefono dello studio
        </Link>
        <Link to="/" className="block mt-6 text-center text-[12px] uppercase tracking-widest2 text-charcoal/50 hover:text-deeptaupe">
          Torna al sito
        </Link>
      </form>
    </div>
  );
}

function Section({ title, description }) {
  return (
    <div className="mb-5">
      <h2 className="font-display text-2xl lg:text-3xl">{title}</h2>
      {description && <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">{description}</p>}
    </div>
  );
}

function Field({ label, value, onChange, textarea = false }) {
  const cls = "w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm focus:border-deeptaupe focus:outline-none";
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">{label}</span>
      {textarea ? (
        <textarea rows={4} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
    </label>
  );
}

function SaveButton({ onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="rounded-full bg-deeptaupe text-ivory text-[13px] uppercase tracking-widest2 font-medium px-10 py-4 transition-colors duration-300 hover:bg-charcoal"
    >
      Salva modifiche
    </button>
  );
}

function HoursEditor({ hours, onChange }) {
  const setDay = (day, windows) => onChange({ ...hours, [day]: windows });
  return (
    <div className="space-y-3" data-testid="admin-hours-editor">
      {WEEK_ORDER.map((day) => {
        const windows = hours[day] || [];
        return (
          <div key={day} className="border border-taupe/25 rounded-2xl p-4">
            <div className="flex items-center justify-between gap-4">
              <label className="flex items-center gap-3 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={windows.length > 0}
                  onChange={(e) => setDay(day, e.target.checked ? [{ start: "09:00", end: "14:00" }] : [])}
                  className="w-4 h-4 accent-[#6B5C50]"
                  data-testid={`admin-hours-toggle-${day}`}
                />
                {DAY_LABELS[day]} disponibile
              </label>
              {windows.length > 0 && (
                <button
                  onClick={() => setDay(day, [...windows, { start: "09:00", end: "14:00" }])}
                  className="text-[12px] uppercase tracking-widest2 text-deeptaupe flex items-center gap-1"
                  data-testid={`admin-hours-add-${day}`}
                >
                  <Plus size={13} /> Fascia
                </button>
              )}
            </div>
            {windows.map((w, i) => (
              <div key={i} className="flex items-center gap-3 mt-3">
                <input
                  type="time"
                  value={w.start}
                  onChange={(e) => {
                    const next = [...windows];
                    next[i] = { ...next[i], start: e.target.value };
                    setDay(day, next);
                  }}
                  data-testid={`admin-hours-start-${day}-${i}`}
                  className="rounded-xl border border-taupe/40 bg-transparent px-3 py-2 text-sm"
                />
                <span className="text-charcoal/50">–</span>
                <input
                  type="time"
                  value={w.end}
                  onChange={(e) => {
                    const next = [...windows];
                    next[i] = { ...next[i], end: e.target.value };
                    setDay(day, next);
                  }}
                  data-testid={`admin-hours-end-${day}-${i}`}
                  className="rounded-xl border border-taupe/40 bg-transparent px-3 py-2 text-sm"
                />
                {windows.length > 1 && (
                  <button onClick={() => setDay(day, windows.filter((_, j) => j !== i))} aria-label="Rimuovi fascia" data-testid={`admin-hours-remove-${day}-${i}`} className="text-red-500/70 hover:text-red-700">
                    <X size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function SlotPreview({ settings }) {
  const days = [];
  const now = romeNow();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
    days.push(d.toLocaleDateString("en-CA"));
  }
  const fake = { duration: 60 };
  return (
    <div className="mt-8 border border-taupe/25 rounded-2xl p-5" data-testid="admin-slot-preview">
      <p className="text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-4">
        Anteprima slot richiedibili (visita da 60 min, prossimi 7 giorni)
      </p>
      <ul className="space-y-2 text-sm">
        {days.map((d) => {
          const slots = slotsForDate(d, fake, settings);
          return (
            <li key={d} className="flex justify-between gap-4 border-b border-taupe/10 pb-1.5">
              <span className="text-charcoal/70">{new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "numeric", month: "short" }).format(new Date(d + "T12:00:00"))}</span>
              <span className="text-charcoal/90">{slots.length ? `${slots[0]}–${slots[slots.length - 1]} · ${slots.length} slot` : "—"}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function BlockedDates({ dates, onChange }) {
  const [value, setValue] = useState("");
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="date"
          value={value}
          min={romeToday()}
          onChange={(e) => setValue(e.target.value)}
          data-testid="admin-blocked-date-input"
          className="rounded-full border border-taupe/40 bg-transparent px-5 py-3 text-sm"
        />
        <button
          onClick={() => {
            if (value && !dates.includes(value)) onChange([...dates, value]);
            setValue("");
          }}
          data-testid="admin-blocked-date-add"
          className="rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-deeptaupe hover:text-ivory transition-colors"
        >
          Blocca data
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        {dates.length === 0 && <p className="text-sm text-charcoal/50">Nessuna data bloccata.</p>}
        {dates.map((d) => (
          <span key={d} className="inline-flex items-center gap-2 bg-peach border border-taupe/30 rounded-full px-4 py-2 text-sm" data-testid={`admin-blocked-chip-${d}`}>
            {new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short", year: "numeric" }).format(new Date(d + "T12:00:00"))}
            <button onClick={() => onChange(dates.filter((x) => x !== d))} aria-label={`Sblocca ${d}`} data-testid={`admin-blocked-remove-${d}`} className="text-charcoal/50 hover:text-red-600">
              <X size={13} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function ServicesTab({ services, reload, flash }) {
  const [editing, setEditing] = useState({});

  const draftOf = (s) => editing[s.id] || s;

  const setField = (s, field, value) => {
    const current = draftOf(s);
    setEditing({ ...editing, [s.id]: { ...current, [field]: value } });
  };

  const save = async (s) => {
    const body = draftOf(s);
    try {
      const { data } = await api.put(`/admin/services/${s.id}`, {
        name: body.name,
        mode: body.mode,
        price: Number(body.price),
        duration: Number(body.duration),
        tagline: body.tagline || "",
        includes: Array.isArray(body.includes) ? body.includes : String(body.includes || "").split("\n").map((x) => x.trim()).filter(Boolean),
        note: body.note || "",
        order: Number(body.order) || 0,
        active: body.active !== false,
      });
      reload(data.services);
      setEditing({ ...editing, [s.id]: null });
      flash("Servizio aggiornato");
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const create = async () => {
    try {
      const { data } = await api.post("/admin/services", {
        name: "Nuovo servizio",
        mode: "studio",
        price: 0,
        duration: 30,
        tagline: "",
        includes: [],
        note: "",
        order: services.length + 1,
        active: false,
      });
      reload(data.services);
      flash("Servizio creato: compilane i campi e salvalo");
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare definitivamente "${s.name}"?`)) return;
    try {
      const { data } = await api.delete(`/admin/services/${s.id}`);
      reload(data.services);
      flash("Servizio eliminato");
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  return (
    <div className="space-y-6">
      <Section title="Servizi e prezzi" description="Modifica nomi, prezzi, durate, contenuti e visibilità. Le modifiche sono immediate sul sito dopo il salvataggio." />
      <button
        onClick={create}
        data-testid="admin-service-create"
        className="inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-peach"
      >
        <Plus size={14} /> Nuovo servizio
      </button>
      {services.map((s) => {
        const d = draftOf(s);
        return (
          <div key={s.id} className="border border-taupe/25 rounded-2xl p-6 space-y-4" data-testid={`admin-service-${s.id}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome" value={d.name} onChange={(v) => setField(s, "name", v)} />
              <label className="block">
                <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Modalità</span>
                <select
                  value={d.mode}
                  onChange={(e) => setField(s, "mode", e.target.value)}
                  data-testid={`admin-service-mode-${s.id}`}
                  className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm"
                >
                  <option value="studio">In studio</option>
                  <option value="online">Online</option>
                </select>
              </label>
              <Field label="Prezzo (€)" value={d.price} onChange={(v) => setField(s, "price", v)} />
              <Field label="Durata (minuti)" value={d.duration} onChange={(v) => setField(s, "duration", v)} />
              <Field label="Intervallo di inizio (minuti)" value={d.interval ?? ""} onChange={(v) => setField(s, "interval", v === "" ? null : Number(v))} />
              <Field label="Ordine di visualizzazione" value={d.order} onChange={(v) => setField(s, "order", v)} />
              <label className="flex items-center gap-3 text-sm self-end pb-3">
                <input
                  type="checkbox"
                  checked={d.active !== false}
                  onChange={(e) => setField(s, "active", e.target.checked)}
                  className="w-4 h-4 accent-[#6B5C50]"
                  data-testid={`admin-service-active-${s.id}`}
                />
                Visibile sul sito
              </label>
            </div>
            <Field label="Tagline" value={d.tagline} onChange={(v) => setField(s, "tagline", v)} />
            <Field label="Cosa include (una voce per riga)" textarea value={Array.isArray(d.includes) ? d.includes.join("\n") : d.includes} onChange={(v) => setField(s, "includes", v)} />
            <Field label="Nota (es. avvisi sul servizio)" textarea value={d.note} onChange={(v) => setField(s, "note", v)} />
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => save(s)}
                data-testid={`admin-service-save-${s.id}`}
                className="rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors"
              >
                Salva servizio
              </button>
              <button
                onClick={() => remove(s)}
                data-testid={`admin-service-delete-${s.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-red-200 text-red-600 text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-red-50"
              >
                <Trash2 size={14} /> Elimina
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
